import {
  ADMIN_REFUND_STATE_MAP,
  ADMIN_SETTLEMENT_STATUS_MAP,
  DerivedInvoiceDisplayState,
  RefundStateSchema,
} from '@neon-one/auctions-types';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from '@/shell';
import { useSettlementStore } from './store';
import { useSettlementActionToast } from './action-toast';
import type { ActionFailureCopy } from './error-copy';
import { countLabel, formatCents } from './format';
import {
  InvoiceStatus,
  designStatus,
  releaseRefunds,
  type InvoiceStatusValue,
  type StatePill,
} from './payment-status';
import type { PaymentInvoice, PaymentLineItem } from './types';

export const REFUND_REASON = 'Refund issued by event staff';

const RELEASE_FAILED: ActionFailureCopy = {
  summary: 'Release failed',
  detail: 'Nothing was released. Try again, or contact support.',
};

// Which invoices can lose a lot. Only `void` is frozen, and only because there
// is nothing left on it to release. Frozen means no entry control renders at
// all — not a disabled one.
//
// "Immutable" for a settled invoice means it never grows, retotals or reopens.
// It has never meant money cannot go back, which is why every status that
// collected anything is in this set.
//
// Custody must never wait on money. A `processing` invoice releases with its
// refund sent against the unconfirmed charge — Neon Pay accepts one and parks
// it until the debit clears — and a `refunded` one releases with no refund at
// all. Both are real classes the backend implements (ADR-014), and excluding
// them here stranded a lot for days behind a payment state that was never
// going to change the answer. Do NOT re-freeze these on the theory that a
// charge in flight might settle against a moved line: the processor holds the
// refund against that charge and reconciles the two itself.
// /* §3.1 which invoices can lose a lot */
const RELEASABLE_FROM: readonly InvoiceStatusValue[] = [
  InvoiceStatus.AwaitingPayment,
  InvoiceStatus.Paid,
  InvoiceStatus.PartiallyRefunded,
  InvoiceStatus.Refunded,
  InvoiceStatus.Processing,
];

export function canReleaseFrom(inv: Pick<PaymentInvoice, 'status'>): boolean {
  return RELEASABLE_FROM.includes(designStatus(inv.status));
}

// A line can be released while it is still ON the invoice. `declined` IS the
// released state, so it is the only disqualifier — a fully refunded line still
// has a lot on it and still has to be able to leave, which is exactly the case
// `lineRefundable` (money remaining) must NOT be used to decide.
export function lineReleasable(li: PaymentLineItem): boolean {
  return !li.declined;
}

// Money still to send back on this line, in cents. Anything already claimed by
// an accepted-but-unsettled refund is NOT owed again: refundedCents stays 0
// until the webhook lands, so this is the only thing standing between a second
// admin action and a real duplicate refund.
export function lineOwedBackCents(li: PaymentLineItem): number {
  const claimed = li.refundedCents + (li.inFlightRefundCents ?? 0);
  return Math.max(0, li.amountCents - claimed);
}

// The lines the refund section can act on — selection, select-all, and the
// payload it submits.
//
// Wider than `lineReleasable` by exactly one case: a line already released
// whose money never came back. A refund that fails leaves the lot gone and the
// bidder still charged, and that line is the only thing a retry can be aimed
// at. Excluding it left `Retry refund` on the attention queue unable to find
// its own line — the one control built to resolve a failed refund reported the
// lot as missing instead — and no other surface calls the money-only endpoint,
// so there was no way to send the money back at all.
//
// Decided by the server's `refundableCents`, never by the invoice's status plus
// the line's arithmetic. A released line shows a full balance whether or not
// its money was ever taken, and the invoice being paid does not mean THIS line
// was: a line released before payment was never part of the charge. Offering
// it spends refund capacity that belongs to the lines that were charged, and
// the last real line then fails at Neon Pay (GT1-586).
export function lineRefundActionable(
  inv: Pick<PaymentInvoice, 'status'>,
  li: PaymentLineItem,
): boolean {
  return lineReleasable(li) || (releaseRefunds(inv.status) && li.refundableCents > 0);
}

// The verb every control in the section takes. Driven by whether releasing off
// THIS invoice sends money back — not by what is owed and not by the lot — so a
// button never promises a refund on money that never arrived, and never
// promises a plain release over a charge that is about to be refunded.
// /* §3.1 wording */
export const ReleaseVerb = { Refund: 'Refund', Release: 'Release' } as const;
export type ReleaseVerbValue = (typeof ReleaseVerb)[keyof typeof ReleaseVerb];

export function releaseVerb(inv: Pick<PaymentInvoice, 'status'>): ReleaseVerbValue {
  return releaseRefunds(inv.status) ? ReleaseVerb.Refund : ReleaseVerb.Release;
}

export function releaseEntryLabel(inv: Pick<PaymentInvoice, 'status'>): string {
  return `${releaseVerb(inv)} items`;
}

export function releaseSectionTitle(inv: Pick<PaymentInvoice, 'status'>): string {
  return `Select items to ${releaseVerb(inv).toLowerCase()}`;
}

/**
 * The confirm button's label, which is the one control whose copy depends on
 * the selection as well as the invoice:
 *
 *   nothing selected                       → the bare verb
 *   no lot in the selection                → `Refund {amount}` / `Remove`, because
 *                                             release is a thing that happens to a
 *                                             LOT: an add-on donation or a covered
 *                                             fee has nothing to hand back to the
 *                                             queue, so promising a release
 *                                             describes an act the operator is not
 *                                             performing
 *   selection moves no money               → `Release` (a line already refunded
 *                                             in full still releases, and no
 *                                             label may promise a refund that
 *                                             isn't one)
 *   money collected, money moving          → `Refund {amount} and release`
 *   never collected                        → `Release {amount}`
 * /* §3.1 wording matrix
 */
export function releaseConfirmLabel(
  inv: Pick<PaymentInvoice, 'status'>,
  selection: { count: number; amountCents: number; releasesLot: boolean },
): string {
  if (selection.count === 0) return releaseVerb(inv);
  if (!selection.releasesLot) {
    return selection.amountCents > 0
      ? `Refund ${formatCents(selection.amountCents)}`
      : 'Remove';
  }
  if (selection.amountCents === 0) return ReleaseVerb.Release;
  return releaseVerb(inv) === ReleaseVerb.Refund
    ? `Refund ${formatCents(selection.amountCents)} and release`
    : `Release ${formatCents(selection.amountCents)}`;
}

// True when the selection contains a lot this action would release. A lot that
// has already left carries no release to promise, so the confirm copy over a
// retried refund reads `Refund {amount}` rather than `Refund {amount} and
// release` — the release half of that sentence already happened.
export function selectionReleasesLot(lines: readonly PaymentLineItem[]): boolean {
  return lines.some((li) => li.kind === 'auction_item' && !li.declined);
}

const REFUND_STATE = RefundStateSchema.enum;
const RELEASED_PILL: StatePill = { label: 'Released', severity: 'secondary' };

// §5.2 — Custody and refund state are separate; the line owns its money label.
export function releasedLinePill(
  inv: Pick<PaymentInvoice, 'status'>,
  li: PaymentLineItem,
): StatePill | null {
  if (li.refundState && li.refundState !== REFUND_STATE.none) return ADMIN_REFUND_STATE_MAP[li.refundState];
  if (li.declined && !releaseRefunds(inv.status)) return RELEASED_PILL;
  const stillMoving = (li.inFlightRefundCents ?? 0) > 0 && li.refundedCents < li.amountCents;
  if (stillMoving) return ADMIN_SETTLEMENT_STATUS_MAP[DerivedInvoiceDisplayState.RefundSettling];
  return li.declined ? RELEASED_PILL : null;
}

export interface ReleasePayload {
  /**
   * The money that actually moves: per line `max(0, amountCents - refundedCents)`.
   * Display only — the request body is the lines plus the reason, and the
   * server refunds each line against its own remaining balance.
   */
  amountCents: number;
  lineItemUuids: string[];
  reason: string;
}

/**
 * null means "there is nothing to act on" — an empty selection, and nothing
 * else. A zero amount is NOT nothing: those lines are already refunded in full
 * and releasing them is the whole point of the operation.
 */
export function buildReleasePayload(
  inv: Pick<PaymentInvoice, 'status'>,
  lines: readonly PaymentLineItem[],
): ReleasePayload | null {
  const actionable = lines.filter((li) => lineRefundActionable(inv, li));
  if (actionable.length === 0) return null;
  // On an invoice that collected money, the amount is what the server will
  // actually send back, which is 0 on a line nothing was collected on even
  // when the line itself is live (GT1-586). On one that collected nothing it
  // stays the face amount, which is what the "Release {amount}" label names.
  const refunds = releaseRefunds(inv.status);
  const amountCents = actionable.reduce(
    (sum, li) => sum + (refunds ? li.refundableCents : lineOwedBackCents(li)),
    0,
  );
  return {
    amountCents,
    lineItemUuids: actionable.map((li) => li.lineId),
    reason: REFUND_REASON,
  };
}

export interface RefundConfirmCopy {
  header: string;
  message: string;
  /** Defaults to the §3.1 confirm label for this invoice and selection. */
  acceptLabel?: string;
}

export interface RefundLinesRequest {
  invoiceUuid: string;
  bidderName: string;
  /**
   * Live getter over store-backed lines, NOT a captured array. Read once for the
   * confirm copy and again on accept, so what is submitted always matches the
   * lines' current state.
   */
  lines: () => readonly PaymentLineItem[];
  copy: (payload: ReleasePayload) => RefundConfirmCopy;
  /** Toast detail on a settled release: bidder name, or the item title. */
  successDetail: string;
  onSuccess?: () => void;
}

/**
 * @param options.group the ConfirmDialog group of the host's own
 * `<ConfirmDialog group="…" />`. Omit for a host mounting an ungrouped one. A
 * mismatch makes confirm.require() silently no-op.
 * Must be called at setup top level — it uses inject-based composables.
 */
export function useInvoiceRefund(options: { group?: string } = {}) {
  const store = useSettlementStore();
  const confirm = useConfirm();
  const toast = useToast();
  const { reportFailure } = useSettlementActionToast();

  function invoiceFor(invoiceUuid: string): PaymentInvoice | null {
    return store.invoices.find((i) => i.invoiceUuid === invoiceUuid) ?? null;
  }

  // A refund already in flight no longer stops the operation. It used to be
  // checked here, invoice-wide, because a pending refund leaves every line at
  // refundedCents 0 and nothing downstream counts anything but succeeded
  // refunds — so a second submission would issue a real duplicate. That guard
  // now lives per LINE, in lineOwedBackCents and in the backend's
  // refund_in_flight class, both reading the line's own obligations.
  //
  // The distinction matters because refund and release are ONE action: an
  // invoice-wide block on the refund was also a block on the release, and an
  // ACH refund takes days to settle. A lot must not sit unsellable that whole
  // time over money Neon Pay has already accepted.
  function refundLines(req: RefundLinesRequest): void {
    // Built from one snapshot: buildReleasePayload drops lines this invoice
    // cannot act on, so classifying the label off the raw selection can promise
    // a release for a payload that carries only an add-on (the lot in it having
    // already gone).
    //
    // An absent invoice narrows the payload rather than widening it: without a
    // status there is no evidence money was collected, and a released line is
    // only refundable on that evidence.
    const inv = invoiceFor(req.invoiceUuid) ?? { status: 'awaiting' as const };
    const previewLines = req.lines().filter((li) => lineRefundActionable(inv, li));
    const preview = buildReleasePayload(inv, previewLines);
    if (!preview) return;
    const copy = req.copy(preview);

    confirm.require({
      ...(options.group ? { group: options.group } : {}),
      message: copy.message,
      header: copy.header,
      icon: 'pi pi-undo',
      rejectProps: { label: 'Cancel', severity: 'secondary', text: true },
      acceptProps: {
        label:
          copy.acceptLabel ??
          releaseConfirmLabel(inv, {
            count: preview.lineItemUuids.length,
            amountCents: preview.amountCents,
            releasesLot: selectionReleasesLot(previewLines),
          }),
        severity: 'danger',
      },
      accept: async () => {
        // Rebuilt from live state: a WS frame can refresh the invoice while the
        // dialog sits open, and a line released underneath it must not be sent
        // again. The invoice is re-read for the same reason — its status is
        // what decides whether an already-released line is still refundable.
        const invNow = invoiceFor(req.invoiceUuid) ?? inv;
        const payload = buildReleasePayload(invNow, req.lines());
        if (!payload) return;
        const { ok, refundedCents, pending, releasedLineItemUuids } =
          await store.refundAndRelease(
            req.invoiceUuid,
            payload.lineItemUuids,
            payload.reason,
          );
        // Report what came BACK, not what was asked for. Every line is its own
        // operation with its own obligation, so a selection can partly
        // succeed — and a toast counting the request called the ones that
        // failed released, while their failures sat in the attention queue
        // contradicting it.
        const missed = payload.lineItemUuids.length - releasedLineItemUuids.length;
        const released = countLabel(releasedLineItemUuids.length, 'item');
        if (ok && missed > 0) {
          toast.warn({
            summary: `Released ${released} of ${payload.lineItemUuids.length}`,
            detail: `${countLabel(missed, 'item')} could not be released. See Needs attention.`,
          });
        } else if (ok) {
          toast.success({
            summary: settledSummary({ refundedCents, pending, released }),
            detail: pending
              ? `Sent to NeonPay. ${req.bidderName} will see it once it settles.`
              : req.successDetail,
          });
        } else {
          // Through the code-keyed copy, so a refusal that has its own sentence
          // (a selection larger than the charge has left) says so rather than
          // reading as a generic failure.
          reportFailure(RELEASE_FAILED);
        }
        if (ok) req.onSuccess?.();
      },
    });
  }

  return { refundLines };
}

// What actually happened, which is not always what the button said: a selection
// of lines already refunded in full moves no money and is reported as a release.
function settledSummary(outcome: {
  refundedCents: number;
  pending: boolean;
  released: string;
}): string {
  if (outcome.refundedCents <= 0) return `Released ${outcome.released}`;
  return outcome.pending
    ? `Refund settling: ${formatCents(outcome.refundedCents)}`
    : `Refunded ${formatCents(outcome.refundedCents)} and released`;
}
